import React, { useCallback, useEffect } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import Typography from "@components/typography";
import Fields from "@components/fields";
import Button from "@components/button";
import { Order } from "@apis/index";
import { toast } from "react-toastify";
import { useModal } from "@context/ModalContext";
import { useSelector } from "react-redux";
import { RootState } from "@store/index";
import generalSettings from "@apis/generalSettings";

interface RejectOrderModalProps {
  orderId: number | string;
  hideModal: () => void;
  onSuccess?: () => void;
  status?: "Cancelled" | "Rejected";
}

const RejectOrderSchema = Yup.object().shape({
  reasonId: Yup.string().nullable(),
  note: Yup.string().when("reasonId", {
    is: (reasonId: string) => !reasonId,
    then: () =>
      Yup.string().required("Please provide a note if no reason is selected"),
    otherwise: () => Yup.string(),
  }),
});

const RejectOrderModal: React.FC<RejectOrderModalProps> = ({
  orderId,
  hideModal,
  onSuccess,
  status = "Rejected",
}) => {
  const { data: cancellations } = useSelector(
    (state: RootState) => state.cancellations,
  );

  const fetchCancellations = useCallback(async () => {
    await generalSettings
      .getByGroup({
        state: "cancellations",
        id: "cancellations",
      })
      .then(() => {})
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchCancellations();
  }, [fetchCancellations]);

  const REJECTION_REASONS = Array.isArray(cancellations)
    ? cancellations
        ?.filter(
          (item: any) =>
            item.infos.status === "true" || item.infos.status === true,
        )
        ?.map((item) => ({ id: item.id, name: item.value }))
    : [];
  return (
    <Formik
      initialValues={{ reasonId: "", note: "" }}
      validationSchema={RejectOrderSchema}
      onSubmit={async (values, { setSubmitting }) => {
        try {
          await Order.rejectOrder(
            orderId,
            values.reasonId ? Number(values.reasonId) : undefined,
            values.note,
            status,
          );
          toast.success(status === "Cancelled" ? "Order cancelled successfully" : "Order rejected successfully");
          if (onSuccess) onSuccess();
          hideModal();
        } catch (error: any) {
          toast.error(error.message || `Failed to ${status === "Cancelled" ? "cancel" : "reject"} order`);
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ isSubmitting, setFieldValue }) => (
        <Form>
          <Typography styleText="font-size:18px;font-weight:600;margin-bottom:12px;color:#ef4444;">
            {status === "Cancelled" ? "Cancel Order" : "Reject Order"}
          </Typography>
          <Typography styleText="font-size:14px;color:#666;margin-bottom:20px;">
            Are you sure you want to {status === "Cancelled" ? "cancel" : "reject"} this order? This action cannot be
            undone.
          </Typography>

          <Fields.DropDown
            name="reasonId"
            label="Rejection Reason"
            options={REJECTION_REASONS}
            titleAsKey="name"
            valueAsKey="id"
            placeholder="Select a reason"
            onChange={(val: any) => setFieldValue("reasonId", val)}
            styleInput="width:96%"
          />
          <br />
          <Fields.TextArea
            name="note"
            label="Additional Note (Optional)"
            placeholder="Add specific details for the customer..."
            rows={3}
          />

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "10px",
              marginTop: "20px",
            }}
          >
            <Button
              title="Cancel"
              variant="outline"
              type="button"
              onClick={hideModal}
            />
            <Button
              title={isSubmitting ? (status === "Cancelled" ? "Cancelling..." : "Rejecting...") : (status === "Cancelled" ? "Cancel Order" : "Reject Order")}
              type="submit"
              disabled={isSubmitting}
              styleButton="background-color: #ef4444; border-color: #ef4444;"
            />
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default RejectOrderModal;
